/**
 * Responsibility: translate keyboard input into the control signals the simulation consumes.
 *
 * Those signals come in two shapes, and both belong here so there is one place that knows which key
 * does what — which is also what makes the deferred gamepad path a matter of adding a producer
 * rather than touching consumers.
 *
 *   - **Axes** are continuous and sampled: how far a control is deflected right now.
 *   - **Actions** are discrete and consumed: something happened once. Cycling aircraft is an action,
 *     because holding the key must not cycle repeatedly.
 *
 * ## Why axes rather than key checks
 *
 * The flight model never learns that a keyboard exists. It receives numbers in -1..1, so a gamepad
 * later means another producer of the same shape. Keyboard only ever produces -1, 0, or 1; an
 * analogue stick would fill in everything between.
 *
 * ## Testability
 *
 * `axesFromPressedKeys` is a pure function and holds all the axis mapping rules, so the interesting
 * part is unit tested. `createKeyboardInput` is the listener plumbing around it and is the only part
 * that touches the DOM.
 */

import { INPUT } from '../config.js';

/**
 * Keys that steer, grouped by the axis and direction they drive.
 *
 * WASD and the arrow keys are listed together deliberately: the requirement is that they
 * behave identically, and the cheapest way to guarantee that is for there to be exactly
 * one table rather than two code paths. Uses `KeyboardEvent.code`, which is layout
 * independent — `key` would report different letters on an AZERTY keyboard.
 */
const KEY_BINDINGS = {
  rollLeft: ['KeyA', 'ArrowLeft'],
  rollRight: ['KeyD', 'ArrowRight'],
  pitchUp: ['KeyW', 'ArrowUp'],
  pitchDown: ['KeyS', 'ArrowDown'],
};

/**
 * Keys that fire a one-off action rather than holding an axis open.
 *
 * Kept separate from `KEY_BINDINGS` because they are handled differently: an action fires on the
 * keydown *transition* only, so holding the key does nothing further.
 */
const ACTION_BINDINGS = {
  cycleAircraft: [INPUT.CYCLE_AIRCRAFT_KEY],
  togglePause: [INPUT.PAUSE_KEY],
  trimDown: [INPUT.TRIM_DOWN_KEY],
  trimUp: [INPUT.TRIM_UP_KEY],

  /*
    Camera framing rather than flight. An action rather than an axis because it is a setting being nudged to a value, not a
    control being held open — and because it is persisted, so a held key ratcheting it to the limit would be a nuisance.
  */
  cameraTiltDown: [INPUT.CAMERA_TILT_DOWN_KEY],
  cameraTiltUp: [INPUT.CAMERA_TILT_UP_KEY],

  /*
    Train mode only, where there are two cameras to switch between. Harmless in the air, where nothing reads
    it — a key that does nothing somewhere is better than a key that means different things depending on where
    you are.
  */
  toggleView: [INPUT.TOGGLE_VIEW_KEY],
};

/** Every bound key, for deciding whether an event is ours to swallow. */
const BOUND_KEYS = new Set([
  ...Object.values(KEY_BINDINGS).flat(),
  ...Object.values(ACTION_BINDINGS).flat(),
]);

/** Which action a key fires, or undefined. */
const ACTION_BY_KEY = new Map(
  Object.entries(ACTION_BINDINGS).flatMap(([action, codes]) =>
    codes.map((code) => [code, action]),
  ),
);

/** Element types that own their keystrokes. */
const TYPING_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT']);

/**
 * Whether a key event belongs to something the player is typing into.
 *
 * The pause menu has a seed field. Bound keys are swallowed with `preventDefault`, so without this
 * check every letter of `WASD` would steer the aircraft instead of reaching the field — and the field
 * would silently refuse to accept them.
 *
 * @param {EventTarget} target
 * @returns {boolean}
 */
export function isTypingTarget(target) {
  if (!target) return false;
  if (target.isContentEditable) return true;
  return TYPING_TAGS.has(target.tagName);
}

/**
 * Whether any key bound to an action is currently held.
 *
 * @param {Set<string>} pressed
 * @param {string} action
 * @returns {boolean}
 */
function isActionHeld(pressed, action) {
  return KEY_BINDINGS[action].some((code) => pressed.has(code));
}

/**
 * Reduce held keys to normalized control axes.
 *
 * Opposing keys cancel rather than one winning. Holding left and right together gives
 * zero, which is both the least surprising outcome and self-correcting — the player
 * releases one and gets what they expected.
 *
 * @param {Set<string>} pressed - Held `KeyboardEvent.code` values.
 * @param {boolean} [invertPitch] - Defaults to the configured preference.
 * @returns {{roll: number, pitch: number}} Components in -1..1. Positive roll banks right;
 *   positive pitch raises the nose.
 */
export function axesFromPressedKeys(pressed, invertPitch = INPUT.INVERT_PITCH) {
  const roll = (isActionHeld(pressed, 'rollRight') ? 1 : 0) - (isActionHeld(pressed, 'rollLeft') ? 1 : 0);
  const rawPitch =
    (isActionHeld(pressed, 'pitchUp') ? 1 : 0) - (isActionHeld(pressed, 'pitchDown') ? 1 : 0);

  return {
    roll,
    pitch: invertPitch ? -rawPitch : rawPitch,
  };
}

/** Axes with everything centred. Returned when nothing is held. */
export const NEUTRAL_AXES = Object.freeze({ roll: 0, pitch: 0 });

/** Shared empty result, so the common case of no actions allocates nothing. */
const EMPTY_ACTIONS = new Set();

/**
 * Attach keyboard listeners and expose the resulting control axes.
 *
 * @param {object} [options]
 * @param {EventTarget} [options.target] - Where to listen. Defaults to `window`.
 * @returns {{getAxes: () => {roll: number, pitch: number}, isIdle: () => boolean, dispose: () => void}}
 */
export function createKeyboardInput({ target = window, invertPitch = INPUT.INVERT_PITCH } = {}) {
  if (!target || typeof target.addEventListener !== 'function') {
    throw new Error('createKeyboardInput: target must support addEventListener');
  }

  /*
    Held here rather than read from config on every sample, so the settings menu can change it while
    flying. `axesFromPressedKeys` still takes it as an argument and stays pure.
  */
  let pitchInverted = Boolean(invertPitch);

  /** Held keys, by `code`. */
  const pressed = new Set();

  /** Actions fired since the last `consumeActions`. */
  const pendingActions = new Set();

  function handleKeyDown(event) {
    if (!BOUND_KEYS.has(event.code)) return;

    /*
      Anything the player is typing into keeps its own keystrokes — except the pause key, which has to
      work from anywhere, including with the cursor sitting in the seed field.
    */
    if (isTypingTarget(event.target) && event.code !== INPUT.PAUSE_KEY) return;

    /*
      Swallow the event so the arrow keys do not also scroll the page. The canvas fills the
      viewport so there is usually nothing to scroll, but this also stops the browser from
      treating arrow presses as caret navigation.
    */
    event.preventDefault();

    /*
      Actions fire on the transition into "held" only. Key repeat delivers keydown continuously, so
      without this check holding the cycle key would race through every aircraft several times a
      second.
    */
    const action = ACTION_BY_KEY.get(event.code);
    if (action && !pressed.has(event.code)) {
      pendingActions.add(action);
    }

    pressed.add(event.code);
  }

  function handleKeyUp(event) {
    if (!BOUND_KEYS.has(event.code)) return;

    /*
      Deleted regardless of where the event came from. A key pressed over the canvas and released over
      a focused field would otherwise stay held forever, and the aircraft would keep turning.
    */
    pressed.delete(event.code);

    if (isTypingTarget(event.target) && event.code !== INPUT.PAUSE_KEY) return;
    event.preventDefault();
  }

  /*
    A key held while the window loses focus never delivers its keyup, which would leave the
    aircraft turning forever with nothing on screen explaining why. Clearing on blur is the
    fix, and it is why input keeps its own state rather than reading it on demand.
  */
  function handleBlur() {
    pressed.clear();

    /*
      Pending actions are dropped too. An action queued just as focus was lost would otherwise fire
      whenever the player came back, which reads as the game doing something on its own.
    */
    pendingActions.clear();
  }

  target.addEventListener('keydown', handleKeyDown);
  target.addEventListener('keyup', handleKeyUp);
  target.addEventListener('blur', handleBlur);

  return {
    /**
     * Control axes for this simulation step.
     *
     * @returns {{roll: number, pitch: number}}
     */
    getAxes() {
      return pressed.size === 0 ? NEUTRAL_AXES : axesFromPressedKeys(pressed, pitchInverted);
    },

    /**
     * Change the pitch convention while flying.
     *
     * @param {boolean} inverted
     */
    setInvertPitch(inverted) {
      pitchInverted = Boolean(inverted);
    },

    /** The current pitch convention. */
    isPitchInverted() {
      return pitchInverted;
    },

    /**
     * Forget every held key.
     *
     * Called when the pause menu opens. Without it, a direction held at the moment of pausing is still
     * held on resume, and the aircraft banks the instant play continues — the keyup happened while the
     * menu had focus and never reached us.
     */
    releaseAll() {
      pressed.clear();
      pendingActions.clear();
    },

    /**
     * Actions fired since the last call, then cleared.
     *
     * Consumed rather than sampled: an action means "this happened once". Reading without clearing
     * would leave the caller responsible for remembering what it had already handled, which is how
     * one keypress ends up cycling three aircraft.
     *
     * @returns {Set<string>} Empty most frames. The same set is reused, so callers should not hold
     *   onto it past the current frame.
     */
    consumeActions() {
      if (pendingActions.size === 0) return EMPTY_ACTIONS;

      const fired = new Set(pendingActions);
      pendingActions.clear();
      return fired;
    },

    /** Whether nothing is currently held. Useful for debug readouts. */
    isIdle() {
      return pressed.size === 0;
    },

    /** Detach listeners. */
    dispose() {
      target.removeEventListener('keydown', handleKeyDown);
      target.removeEventListener('keyup', handleKeyUp);
      target.removeEventListener('blur', handleBlur);
      pressed.clear();
    },
  };
}
